import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonIcon,
  IonContent,
  IonCard,
  IonCardContent,
  IonSpinner,
  IonText,
  IonAlert,
  IonSelect,
  IonSelectOption,
  IonToggle,
} from '@ionic/react';
import {
  createOutline,
  star,
  starOutline,
  radioOutline,
  locationOutline,
  chevronForwardOutline,
  statsChartOutline,
  businessOutline,
  leafOutline,
  notificationsOutline,
  optionsOutline,
  navigateOutline,
  closeCircleOutline,
  cloudOfflineOutline,
} from 'ionicons/icons';
import { describeCondition, supportsAlerts } from '../../domain/Alert';
import { enabledOfKind } from '../../domain/Component';
import { LOCATION_SOURCE_LABEL, weatherLocationFor } from '../../domain/DeviceLocation';
import { useDeviceDetail } from '../../application/use-cases/devices/useDeviceDetail';
import StationComponentsCard from '../components/station/StationComponentsCard';
import StationValves from '../components/station/StationValves';
import DeviceShareCard from '../components/DeviceShareCard';
import LocationPicker from '../components/map/LocationPicker';
import { ConnectivityBadge, ConnectivityBanner } from '../components/ConnectivityStatus';
import { formatRelative } from '../../domain/WeatherStation';
import DeleteRow from '../components/DeleteRow';
import './Settings.css';
import './DeviceDetail.css';

function formatDuration(startIso: string, endIso: string | null): string {
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const minutes = Math.max(1, Math.round((end - start) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

const DeviceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const {
    device,
    sites,
    lot,
    alertEvents,
    loading,
    error,
    showRename,
    setShowRename,
    showDeleteConfirm,
    setShowDeleteConfirm,
    locationError,
    handleToggleFavorite,
    handleToggleConnectivityNotifications,
    handleRename,
    handleUpdateSite,
    handleUpdateLocation,
    handleUseMyLocation,
    handleClearLocation,
    locating,
    handleDelete,
    componentError,
    handleAddComponent,
    handleRenameComponent,
    handleToggleComponent,
    handleRemoveComponent,
  } = useDeviceDetail(id);

  const where = device ? weatherLocationFor(device, sites) : null;
  const canEdit = device?.access !== 'view';

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/devices" />
          </IonButtons>
          <IonTitle>Configurar estación</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}

        {!loading && error && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}

        {!loading && !error && !device && <IonText color="medium">Estación no encontrada.</IonText>}

        {device && (
          <>
            {/* Quién es: nombre, estado y accesos rápidos. */}
            <div className="config-hero">
              <span className="config-hero-icon">
                <IonIcon icon={radioOutline} />
              </span>
              <div className="config-hero-body">
                <div className="config-hero-title">
                  <h2>{device.name}</h2>
                  <ConnectivityBadge device={device} />
                </div>
                <p>
                  {device.lastSeenAt ? `Último dato ${formatRelative(device.lastSeenAt)}` : 'Todavía no mandó datos'}
                  {device.access !== 'owner' &&
                    ` · compartida por ${device.ownerName} (${device.access === 'control' ? 'podés controlarla' : 'solo lectura'})`}
                </p>
                <div className="config-hero-actions">
                  {canEdit && (
                    <button onClick={() => setShowRename(true)}>
                      <IonIcon icon={createOutline} />
                      Renombrar
                    </button>
                  )}
                  <button
                    onClick={handleToggleFavorite}
                    aria-pressed={device.isFavorite}
                    className={device.isFavorite ? 'is-on' : ''}
                  >
                    <IonIcon icon={device.isFavorite ? star : starOutline} />
                    {device.isFavorite ? 'Principal' : 'Hacer principal'}
                  </button>
                  <button onClick={() => history.push(`/app/stations/${device.id}`)}>
                    <IonIcon icon={statsChartOutline} />
                    Ver datos
                  </button>
                </div>
              </div>
            </div>

            <ConnectivityBanner device={device} />

            {/* Dónde está: campo, lote y el punto exacto en el mapa, todo junto. */}
            <section className="settings-section">
              <p className="settings-eyebrow">Dónde está</p>
              <IonCard>
                <IonCardContent>
                  <div className="settings-row">
                    <IonIcon icon={businessOutline} />
                    <div className="config-row-body">
                      <p className="settings-row-label">Campo</p>
                      {canEdit ? (
                        <IonSelect
                          aria-label="Campo"
                          interface="popover"
                          placeholder="Sin campo"
                          value={device.siteId}
                          onIonChange={(e) => handleUpdateSite(e.detail.value)}
                        >
                          {sites.map((site) => (
                            <IonSelectOption key={site.id} value={site.id}>
                              {site.name}
                            </IonSelectOption>
                          ))}
                        </IonSelect>
                      ) : (
                        <p className="settings-row-value">{sites.find((s) => s.id === device.siteId)?.name ?? 'Sin campo'}</p>
                      )}
                    </div>
                    <button className="config-link" onClick={() => history.push('/app/sites')}>
                      Editar campos
                    </button>
                  </div>

                  <button
                    className="settings-link-row"
                    onClick={() => history.push(lot ? `/app/lots/${lot.id}` : '/app/lots')}
                  >
                    <IonIcon icon={leafOutline} />
                    <span className="config-row-body">
                      <span className="settings-row-label">Lote</span>
                      <span className="settings-row-value">{lot ? lot.name : 'No está en ningún lote'}</span>
                      {!lot && <span className="config-hint">Se suma desde el lote, al editarlo.</span>}
                    </span>
                    <IonIcon icon={chevronForwardOutline} className="chevron" />
                  </button>

                  <div className="config-location">
                    <div className="settings-row">
                      <IonIcon icon={locationOutline} />
                      <div className="config-row-body">
                        <p className="settings-row-label">Ubicación de la estación</p>
                        <p className="settings-row-value">
                          {!where
                            ? 'Sin ubicación: no se puede mostrar su clima'
                            : where.source === 'site'
                              ? `La de su campo (${where.siteName})`
                              : LOCATION_SOURCE_LABEL[where.source]}
                        </p>
                      </div>
                    </div>
                    {canEdit ? (
                      <>
                        <p className="config-hint">
                          {device.locationSource === 'gps'
                            ? 'La reporta el propio equipo por GPS y se actualiza sola. Si la cambiás a mano, el próximo mensaje del equipo la vuelve a pisar.'
                            : 'Buscá la localidad (o pegá coordenadas) o tocá el mapa para marcar dónde está instalada. De acá sale su clima y su pronóstico; si no la marcás, se usa la del campo.'}
                        </p>
                        <div className="location-actions">
                          <IonButton size="small" fill="outline" disabled={locating} onClick={handleUseMyLocation}>
                            <IonIcon icon={navigateOutline} slot="start" />
                            {locating ? 'Buscando…' : 'Usar mi ubicación actual'}
                          </IonButton>
                          {device.lat !== null && device.lng !== null && (
                            <IonButton size="small" fill="clear" color="medium" onClick={handleClearLocation}>
                              <IonIcon icon={closeCircleOutline} slot="start" />
                              Quitar
                            </IonButton>
                          )}
                        </div>
                        <LocationPicker lat={device.lat} lng={device.lng} onChange={handleUpdateLocation} />
                        {locationError && (
                          <IonText color="danger">
                            <p style={{ fontSize: 13 }}>{locationError}</p>
                          </IonText>
                        )}
                      </>
                    ) : (
                      device.lat !== null &&
                      device.lng !== null && (
                        <p className="config-hint">
                          {device.lat.toFixed(4)}, {device.lng.toFixed(4)}
                        </p>
                      )
                    )}
                  </div>
                </IonCardContent>
              </IonCard>
            </section>

            <section className="settings-section">
              <StationComponentsCard
                device={device}
                error={componentError}
                onAdd={handleAddComponent}
                onRename={handleRenameComponent}
                onToggle={handleToggleComponent}
                onRemove={handleRemoveComponent}
              />
              {enabledOfKind(device.components, 'valve').length > 0 && (
                <StationValves
                  key={enabledOfKind(device.components, 'valve').map((c) => `${c.channel}:${c.name}`).join(',')}
                  deviceId={device.id}
                  canControl={canEdit}
                />
              )}
            </section>

            <section className="settings-section">
              <p className="settings-eyebrow">Avisos</p>
              <IonCard>
                <IonCardContent>
                  <div className="settings-row settings-row-toggle">
                    <IonIcon icon={cloudOfflineOutline} />
                    <span className="config-row-body">
                      <span className="settings-row-toggle-label">Avisarme si se desconecta</span>
                      <span className="config-hint">Cuando lleva más de 30 min sin reportar, y cuando vuelve</span>
                    </span>
                    <IonToggle
                      checked={device.notifyConnectivity}
                      onIonChange={(e) => handleToggleConnectivityNotifications(e.detail.checked)}
                      aria-label="Avisarme si se desconecta"
                    />
                  </div>
                  {supportsAlerts(device.components) && canEdit && (
                    <button className="settings-link-row" onClick={() => history.push(`/app/devices/${device.id}/thresholds`)}>
                      <IonIcon icon={optionsOutline} />
                      <span className="config-row-body">
                        <span className="settings-row-value">Criterios</span>
                        <span className="config-hint">
                          {device.customThresholds ? 'Ajustados para esta estación' : 'Pulverización, helada y humedad'}
                        </span>
                      </span>
                      <IonIcon icon={chevronForwardOutline} className="chevron" />
                    </button>
                  )}
                  {supportsAlerts(device.components) && (
                    <button className="settings-link-row" onClick={() => history.push(`/app/alerts?device=${device.id}`)}>
                      <IonIcon icon={notificationsOutline} />
                      <span className="config-row-body">
                        <span className="settings-row-value">Alertas</span>
                        <span className="config-hint">Qué te avisa esta estación</span>
                      </span>
                      <IonIcon icon={chevronForwardOutline} className="chevron" />
                    </button>
                  )}
                </IonCardContent>
              </IonCard>
            </section>

            {device.access === 'owner' && (
              <section className="settings-section">
                <p className="settings-eyebrow">Compartir</p>
                <IonCard>
                  <IonCardContent>
                    <DeviceShareCard deviceId={device.id} />
                  </IonCardContent>
                </IonCard>
              </section>
            )}

            {alertEvents.length > 0 && (
              <section className="settings-section">
                <p className="settings-eyebrow">Historial de alertas</p>
                <IonCard>
                  <IonCardContent className="alert-history-list">
                    {alertEvents.map((event) => (
                      <div className="alert-history-row" key={event.id}>
                        <div>
                          <p className="alert-history-main">
                            {event.alertName ?? event.conditions.map((c) => describeCondition(c)).join(' y ')}
                          </p>
                          {event.alertName && (
                            <p className="alert-history-meta">{event.conditions.map((c) => describeCondition(c, c.value)).join(' y ')}</p>
                          )}
                          <p className="alert-history-meta">
                            {new Date(event.startedAt).toLocaleString('es-AR')}
                            {!event.endedAt && ' · en curso'}
                          </p>
                        </div>
                        <span className={`alert-history-duration ${!event.endedAt ? 'ongoing' : ''}`}>
                          {formatDuration(event.startedAt, event.endedAt)}
                        </span>
                      </div>
                    ))}
                  </IonCardContent>
                </IonCard>
              </section>
            )}

            {device.access === 'owner' && (
              <section className="settings-section">
                <DeleteRow
                  title="Eliminar estación"
                  hint="Se borran sus sensores, horarios de riego y alertas"
                  onClick={() => setShowDeleteConfirm(true)}
                />
              </section>
            )}
          </>
        )}

        <IonAlert
          isOpen={showRename}
          onDidDismiss={() => setShowRename(false)}
          header="Renombrar estación"
          inputs={[{ name: 'name', type: 'text', value: device?.name, placeholder: 'Nombre' }]}
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Guardar', handler: (data) => handleRename(data.name ?? '') },
          ]}
        />

        <IonAlert
          isOpen={showDeleteConfirm}
          onDidDismiss={() => setShowDeleteConfirm(false)}
          header="¿Eliminar estación?"
          message="Se borran sus sensores, horarios de riego y alertas. Esta acción no se puede deshacer."
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Eliminar', role: 'destructive', handler: handleDelete },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default DeviceDetail;
